/**
 * AsistenciaApiClient — HTTP client para /api/v1/businesses/{businessId}/asistencia.
 * Todos los endpoints están acotados a un negocio específico.
 *
 * @module infrastructure/api/AsistenciaApiClient
 */

import { ApiBase } from './ApiBase.js';

function base(businessId) {
  return `/api/v1/businesses/${businessId}/asistencia`;
}

export const AsistenciaApiClient = {
  /**
   * Lista paginada de registros con filtros opcionales.
   * @param {string} businessId
   * @param {object} opts - { page, pageSize, colaboradorId, tipo, fechaDesde, fechaHasta }
   */
  async list(businessId, opts = {}) {
    const { page = 1, pageSize = 30, colaboradorId, tipo, fechaDesde, fechaHasta } = opts;
    let url = `${base(businessId)}?page=${page}&pageSize=${pageSize}`;
    if (colaboradorId) url += `&colaboradorId=${encodeURIComponent(colaboradorId)}`;
    if (tipo) url += `&tipo=${encodeURIComponent(tipo)}`;
    if (fechaDesde) url += `&fechaDesde=${encodeURIComponent(fechaDesde)}`;
    if (fechaHasta) url += `&fechaHasta=${encodeURIComponent(fechaHasta)}`;
    return ApiBase.get(url);
  },

  /**
   * LA PUERTA DEL CONTEO (ASIS-CONTEO-EXIGE-T1). PROPUESTA de ruta: A todavía no publicó el
   * contrato (#17660), así que este nombre queda declarado en el PR para que lo confirme o lo
   * cambie. Mientras la ruta no exista, la respuesta es 404 y la pantalla lo dice sin frenar el
   * conteo (application/conteo/puertaDelConteo.js -> porQueNoGuarda).
   *
   * El cuerpo se arma aquí, en un solo sitio, y en camelCase (ASP.NET no liga snake_case):
   *   { contadorId, area, entrada: {metodo:'auto_conteo', penalizacionMin}, equipo: [{colaboradorId, vino}] }
   * El autor NUNCA viaja en el cuerpo: sale del token (nexo-api 309).
   */
  async puertaDelConteo(businessId, { contadorId, area, entrada = null, equipo = [] } = {}) {
    const cuerpo = { contadorId, area, equipo: equipo.map((e) => ({ colaboradorId: e.colaboradorId, vino: Boolean(e.vino) })) };
    if (entrada) cuerpo.entrada = { metodo: 'auto_conteo', penalizacionMin: entrada.minutos };
    return ApiBase.post(`${base(businessId)}/puerta-conteo`, cuerpo);
  },

  /** Resumen de asistencia del día actual (presente / ausente por colaborador). */
  async resumenHoy(businessId) {
    return ApiBase.get(`${base(businessId)}/hoy`);
  },

  /**
   * Auditoria de asistencia por rango Colombia. Calcula tardanzas, exceso
   * almuerzo, horas efectivas y estado del dia respetando horario programado.
   * @param {string} businessId
   * @param {{ desde: string, hasta: string, colaboradorId?: string }} opts
   * @returns {Promise<{ ok:boolean, data?:{ data: { desde, hasta, totalFilas, ... , items: [] } } }>}
   */
  async auditoria(businessId, { desde, hasta, colaboradorId } = {}) {
    if (!desde || !hasta) throw new Error('auditoria requiere { desde, hasta } YYYY-MM-DD');
    let url = `${base(businessId)}/auditoria?desde=${encodeURIComponent(desde)}&hasta=${encodeURIComponent(hasta)}`;
    if (colaboradorId) url += `&colaboradorId=${encodeURIComponent(colaboradorId)}`;
    return ApiBase.get(url);
  },

  /**
   * Dias ya CLASIFICADOS por el detector `fn_detectar_anomalias_asistencia`
   * (VIS-T2 parte 2, endpoint de Argy-A en bitacora #3843).
   *
   * La diferencia con `auditoria()` no es de datos, es de AUTORIDAD: auditoria trae
   * los minutos crudos y quien mira decide que significan; esto trae la clasificacion
   * ya hecha en la BD, con su porque en lenguaje humano. Por eso la cuadricula pinta
   * con esto y no vuelve a clasificar por su cuenta: dos verdades sobre el mismo dia
   * es peor que una.
   *
   * @param {string} businessId
   * @param {{ desde: string, hasta: string, soloAnomalias?: boolean }} opts
   * @returns {Promise<{ok:boolean, data?:{data:{desde,hasta,totalFilas,resumenPorClasificacion,items:[]}}}>}
   */
  async anomalias(businessId, { desde, hasta, soloAnomalias = false } = {}) {
    if (!desde || !hasta) throw new Error('anomalias requiere { desde, hasta } YYYY-MM-DD');
    let url = `${base(businessId)}/anomalias?desde=${encodeURIComponent(desde)}&hasta=${encodeURIComponent(hasta)}`;
    if (soloAnomalias) url += '&soloAnomalias=true';
    return ApiBase.get(url);
  },

  /**
   * Registra una entrada o salida (modo admin, con tipo explícito).
   * @param {string} businessId
   * @param {{ colaboradorId: string, tipo: 'entrada'|'salida', notas?: string }} payload
   */
  async registrar(businessId, payload) {
    return ApiBase.post(base(businessId), payload);
  },

  // ── Kiosk ─────────────────────────────────────────────────────────────────

  /** Lista de colaboradores activos para la pantalla kiosk. */
  async kiosk(businessId) {
    return ApiBase.get(`${base(businessId)}/kiosk`);
  },

  /**
   * Kiosk: valida PIN y registra auto-detección de entrada/salida.
   * @param {string} businessId
   * @param {{ colaboradorId: string, pin: string }} payload
   */
  async marcar(businessId, payload) {
    // PANEL-PIN-UNIFICADO-T1: un 429 (PIN frenado) vuelve de una para decirselo a la persona, sin reintentar.
    return ApiBase.post(`${base(businessId)}/marcar`, payload, { reintentar429: false });
  },

  /**
   * Admin: establece el PIN de un colaborador.
   * @param {string} businessId
   * @param {string} colaboradorId
   * @param {string} pin  4-6 digitos
   */
  async setPin(businessId, colaboradorId, pin) {
    return ApiBase.patch(`/api/v1/businesses/${businessId}/colaboradores/${colaboradorId}/pin`, { pin });
  },

  // ── Edicion admin ─────────────────────────────────────────────────────────

  /**
   * Admin: corrige la HORA de un marcaje SIN pisar la marca original
   * (ASIST-FIX, contrato bit #4249). La correccion queda como capa auditada:
   * attendance_correcciones guarda quien, cuando, motivo y valor anterior.
   * Quien pinte tardanzas debe leer v_asistencia_efectiva (timestamp_efectivo).
   *
   * @param {string} businessId
   * @param {string} id  UUID del registro attendance
   * @param {{ timestampCorregido: string, motivo: string }} payload  (sin corregidoPor: el autor lo pone la API del token, #17507)
   *   motivo: obligatorio, minimo 5 caracteres (lo exige tambien la BD via CHECK)
   */
  async correccion(businessId, id, payload) {
    return ApiBase.patch(`${base(businessId)}/${id}/correccion`, payload);
  },

  /**
   * Admin: actualiza NOTAS de un registro. NO usar para corregir horas:
   * este PATCH pisa timestamp_evento sin dejar rastro (el hueco que ASIST-FIX
   * cerro). Para horas usar correccion().
   *
   * Manda SOLO {notas}: la hora nunca viaja por aqui. Desde api#152 TimestampEvento es nullable (COALESCE) y las
   * notas van solas; el aviso viejo de #4423 (mandar siempre el timestampEvento vigente) era para el backend anterior.
   * Medido 16-oct (#28569): la API servida es la nueva. Si un llamador pasa timestampEvento, aqui se descarta.
   *
   * @param {string} businessId
   * @param {string} id  UUID del registro
   * @param {{ notas: string }} payload
   */
  async patch(businessId, id, payload) {
    return ApiBase.patch(`${base(businessId)}/${id}`, { notas: payload?.notas ?? '' });
  },

  /**
   * MARCAS QUE ARGEMIRO PROPONE (ARG-MARCAJE-PROPONE-T1, nexo-api 329, contrato #18682). Sin fechas: los 7 dias hasta
   * ayer. data = {desde, hasta, propuestas: [{colaboradorId, nombre, fecha, tipo, horaSugerida, fuente, motivo}]}.
   */
  async marcasPropuestas(businessId, { desde, hasta } = {}) {
    const q = new URLSearchParams();
    if (desde) q.set('desde', desde);
    if (hasta) q.set('hasta', hasta);
    const s = q.toString();
    return ApiBase.get(`${base(businessId)}/marcas-propuestas${s ? '?' + s : ''}`);
  },

  /**
   * El jefe autoriza UNA propuesta: la marca queda escrita con su nombre (el autor sale del token, nunca del cuerpo).
   * El cuerpo lo arma application/asistencia/marcasPropuestas.js (cuerpoAutorizar), en camelCase.
   * 409 NO_ES_PROPUESTA; 400 FALTAN_DATOS | HORA_INVALIDA | HORA_REQUERIDA.
   */
  async autorizarMarcaPropuesta(businessId, cuerpo) {
    return ApiBase.post(`${base(businessId)}/marcas-propuestas/autorizar`, cuerpo);
  },

  /** Admin: elimina un registro de asistencia. */
  async delete(businessId, id) {
    return ApiBase.del(`${base(businessId)}/${id}`);
  }
};
